import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import SEOHead from '../components/SEOHead';

const TermsOfUse = () => {
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://donorshouse.com';

  const breadcrumbs = [
    { name: 'Home', url: siteUrl },
    { name: 'Terms of Use', url: `${siteUrl}/terms` }
  ];

  return (
    <>
      <SEOHead
        title="Terms of Use | DonorsHouse - Service Terms and Conditions"
        description="Read DonorsHouse's Terms of Use to understand the rules and guidelines for using our platform. Learn about user responsibilities, medical disclaimers, and service terms."
        keywords="terms of use, terms and conditions, service terms, user agreement, platform rules, medical disclaimer"
        breadcrumbs={breadcrumbs}
      />
      <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Use</h1>
        
        <Card className="shadow-md mb-6">
          <CardHeader>
            <CardTitle className="text-gray-900">1. Acceptance of Terms</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-700">
            <p>
              By accessing or using DonorsHouse ("the Service"), you agree to be bound by these Terms of Use. 
              If you do not agree to these terms, please do not use the Service.
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-md mb-6">
          <CardHeader>
            <CardTitle className="text-gray-900">2. Description of Service</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-700 space-y-3">
            <p>DonorsHouse provides a platform to:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Connect blood and organ donors with recipients</li>
              <li>Share information about donation centers worldwide</li>
              <li>Provide educational resources about organ and blood donation</li>
              <li>Facilitate communication between registered users</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="shadow-md mb-6">
          <CardHeader>
            <CardTitle className="text-gray-900">3. User Responsibilities</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-700 space-y-3">
            <p>You agree to:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Provide accurate and truthful information</li>
              <li>Keep your account credentials secure</li>
              <li>Comply with all applicable laws and regulations</li>
              <li>Not misuse or abuse the Service</li>
              <li>Respect the privacy and rights of other users</li>
            </ul>
          </CardContent>
        </Card>

        <Card className="shadow-md mb-6">
          <CardHeader>
            <CardTitle className="text-gray-900">4. Medical Disclaimer</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-700">
            <p className="font-semibold mb-2">IMPORTANT:</p>
            <p>
              DonorsHouse is an information platform only. We do not provide medical advice, diagnosis, or treatment. 
              All medical decisions should be made in consultation with qualified healthcare professionals. The information 
              provided about organ donation regulations is for educational purposes and may not reflect current laws. 
              Always verify with official government sources and medical institutions.
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-md mb-6">
          <CardHeader>
            <CardTitle className="text-gray-900">5. Verification and Safety</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-700">
            <p>
              While we make reasonable efforts to verify donation centers, we cannot guarantee the accuracy of all 
              information. Users should independently verify all information before making donation decisions. 
              DonorsHouse is not responsible for the actions of users or third-party medical facilities.
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-md mb-6">
          <CardHeader>
            <CardTitle className="text-gray-900">6. Intellectual Property</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-700">
            <p>
              All content, features, and functionality of the Service are owned by DonorsHouse and are protected 
              by international copyright, trademark, and other intellectual property laws.
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-md mb-6">
          <CardHeader>
            <CardTitle className="text-gray-900">7. Limitation of Liability</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-700">
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, DONORSHOUSE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
              SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY 
              OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-md mb-6">
          <CardHeader>
            <CardTitle className="text-gray-900">8. Indemnification</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-700">
            <p>
              You agree to indemnify and hold harmless DonorsHouse, New Abilities Foundation, and their affiliates 
              from any claims, damages, losses, liabilities, and expenses arising from your use of the Service or 
              violation of these Terms.
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-md mb-6">
          <CardHeader>
            <CardTitle className="text-gray-900">9. Termination</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-700">
            <p>
              We reserve the right to terminate or suspend your account at any time, without prior notice, for 
              conduct that we believe violates these Terms or is harmful to other users, us, or third parties, 
              or for any other reason.
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-md mb-6">
          <CardHeader>
            <CardTitle className="text-gray-900">10. Governing Law</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-700">
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the United States, 
              without regard to its conflict of law provisions.
            </p>
          </CardContent>
        </Card>

        <Card className="shadow-md">
          <CardHeader>
            <CardTitle className="text-gray-900">11. Contact Information</CardTitle>
          </CardHeader>
          <CardContent className="text-gray-700">
            <p>For questions about these Terms, contact:</p>
            <p className="mt-2 font-semibold">https://newabilities.org/contact</p>
          </CardContent>
        </Card>

        <p className="text-center text-gray-600 mt-8">Last Updated: January 2025</p>
      </div>
    </div>
    </>
  );
};

export default TermsOfUse;
